<template>
  <div :class="['cf', 'cf-' + mode, { compact }]" :style="vars">
    <div v-if="result" class="cf-done" role="status">
      <div class="cf-brand"><img v-if="brand.logo_url" :src="brand.logo_url" alt="" class="cf-logo"><span v-else class="cf-monogram" aria-hidden="true">{{ monogram }}</span><span class="cf-brand-name">{{ brandName }}</span></div>
      <div class="cf-done-mark" aria-hidden="true"><i class="mdi mdi-check"></i></div>
      <h1 ref="done" class="cf-headline" tabindex="-1">Thank you</h1>
      <p class="cf-intro">{{ result.thank_you || content.thank_you || defaultThanks }}</p>
      <div v-if="result.withdrawal_url" class="cf-withdraw">
        <p><strong>Changed your mind later?</strong> Keep this link to withdraw your feedback or hide your details at any time:</p>
        <div class="cf-link-row">
          <input :value="result.withdrawal_url" readonly class="cf-link" aria-label="Your withdrawal link" @focus="$event.target.select()">
          <button type="button" class="cf-button small" @click="copyLink">{{ copied ? 'Copied' : 'Copy' }}</button>
        </div>
      </div>
      <a v-if="attribution" :href="landingUrl" class="cf-attribution" target="_blank" rel="noopener">Collected with TrustClap</a>
    </div>

    <form v-else class="cf-form" novalidate :inert="mode === 'preview' ? true : null" :aria-hidden="mode === 'preview' ? 'true' : null"
          @submit.prevent="submit">
      <div class="cf-brand"><img v-if="brand.logo_url" :src="brand.logo_url" alt="" class="cf-logo"><span v-else class="cf-monogram" aria-hidden="true">{{ monogram }}</span><span class="cf-brand-name">{{ brandName }}</span></div>
      <h1 class="cf-headline">{{ content.headline || 'How was your experience with us?' }}</h1>
      <p v-if="content.intro" class="cf-intro">{{ content.intro }}</p>
      <p class="cf-time"><i class="mdi mdi-clock-outline" aria-hidden="true"></i> About two minutes · No account needed</p>

      <div v-if="error" class="cf-error-box" role="alert">{{ error }}</div>

      <div v-for="(question, index) in questions" :key="question.id" class="cf-field">
        <label :for="fieldId('q' + index)" class="cf-label">{{ question.label }}<span v-if="!question.required" class="cf-optional"> (optional)</span></label>
        <span v-if="question.help" :id="fieldId('qh' + index)" class="cf-help">{{ question.help }}</span>
        <textarea :id="fieldId('q' + index)" v-model="answers[question.id]" :class="['cf-input', 'cf-textarea', { invalid: errors['q:' + question.id] }]"
                  :rows="compact ? 2 : 4" :maxlength="maxChars" :required="question.required"
                  :aria-describedby="question.help ? fieldId('qh' + index) : null"
                  :aria-invalid="errors['q:' + question.id] ? 'true' : null"
                  :placeholder="mode === 'preview' ? 'Your answer…' : ''"></textarea>
        <div class="cf-count" aria-live="off">{{ (answers[question.id] || '').length }} / {{ maxChars }}</div>
        <span v-if="errors['q:' + question.id]" class="cf-field-error">{{ errors['q:' + question.id] }}</span>
      </div>

      <div v-if="fields.rating !== 'off'" class="cf-field">
        <StarRating v-model="rating" input :small="compact" :name="fieldId('rating')" :required="fields.rating === 'required'" :disabled="mode === 'preview'" />
        <span v-if="errors.rating" class="cf-field-error">{{ errors.rating }}</span>
      </div>

      <div v-if="hasAboutYou" class="cf-group">
        <h3 class="cf-group-title">About you</h3>
        <div v-if="fields.name !== 'off'" class="cf-field">
          <label :for="fieldId('name')" class="cf-label">Your name<span v-if="!nameRequired" class="cf-optional"> (optional)</span></label>
          <input :id="fieldId('name')" v-model="name" type="text" autocomplete="name" maxlength="120" :class="['cf-input', { invalid: errors.name }]" :required="nameRequired">
          <span v-if="errors.name" class="cf-field-error">{{ errors.name }}</span>
        </div>
        <div v-if="fields.title !== 'off'" class="cf-field">
          <label :for="fieldId('title')" class="cf-label">Job title<span v-if="fields.title !== 'required'" class="cf-optional"> (optional)</span></label>
          <input :id="fieldId('title')" v-model="jobTitle" type="text" autocomplete="organization-title" maxlength="120" :class="['cf-input', { invalid: errors.title }]">
          <span v-if="errors.title" class="cf-field-error">{{ errors.title }}</span>
        </div>
        <div v-if="fields.company !== 'off'" class="cf-field">
          <label :for="fieldId('company')" class="cf-label">Company<span v-if="fields.company !== 'required'" class="cf-optional"> (optional)</span></label>
          <input :id="fieldId('company')" v-model="company" type="text" autocomplete="organization" maxlength="120" :class="['cf-input', { invalid: errors.company }]">
          <span v-if="errors.company" class="cf-field-error">{{ errors.company }}</span>
        </div>
        <div v-if="fields.photo !== 'off'" class="cf-field">
          <span class="cf-label" :id="fieldId('photo-label')">Photo<span v-if="fields.photo !== 'required'" class="cf-optional"> (optional)</span></span>
          <div class="cf-photo">
            <img v-if="photoUrl" :src="photoUrl" alt="Your chosen photo" class="cf-photo-thumb">
            <span v-else class="cf-photo-thumb empty" aria-hidden="true"><i class="mdi mdi-account-outline"></i></span>
            <label class="cf-button ghost small cf-file">
              <input type="file" accept="image/png,image/jpeg,image/webp" :aria-labelledby="fieldId('photo-label')" @change="choosePhoto">
              {{ photoUrl ? 'Choose another' : 'Add a photo' }}
            </label>
            <button v-if="photoUrl" type="button" class="cf-link-button" @click="removePhoto">Remove</button>
          </div>
          <span v-if="errors.photo || photoError" class="cf-field-error">{{ errors.photo || photoError }}</span>
        </div>
        <div v-if="fields.contact !== 'off'" class="cf-field">
          <label :for="fieldId('contact')" class="cf-label">Email<span v-if="fields.contact !== 'required'" class="cf-optional"> (optional)</span></label>
          <span :id="fieldId('contact-help')" class="cf-help">{{ named ? 'Only ' + brandName + ' sees this.' : 'Only we can see this.' }} It is never shown publicly.</span>
          <input :id="fieldId('contact')" v-model="contactEmail" type="email" autocomplete="email" maxlength="255"
                 :aria-describedby="fieldId('contact-help')" :class="['cf-input', { invalid: errors.contact }]">
          <span v-if="errors.contact" class="cf-field-error">{{ errors.contact }}</span>
        </div>
      </div>

      <fieldset :class="['cf-consent', { invalid: errors.publish }]">
        <legend class="cf-consent-title">May {{ named ? brandName : 'we' }} publish your answers as a testimonial?</legend>
        <p class="cf-help">Your feedback is sent either way. You can change your mind later with the link you get after sending.</p>
        <label class="cf-choice"><input v-model="publish" type="radio" :name="fieldId('publish')" :value="true"> Yes, you may publish my answers</label>
        <div v-if="publish === true && detailOptions.length" class="cf-details">
          <p class="cf-details-title">Show with my words:</p>
          <label v-for="option in detailOptions" :key="option.key" class="cf-check">
            <input v-model="show[option.key]" type="checkbox"> Show my {{ option.label }}
          </label>
        </div>
        <label class="cf-choice"><input v-model="publish" type="radio" :name="fieldId('publish')" :value="false"> No, keep my feedback private</label>
        <span v-if="errors.publish" class="cf-field-error">{{ errors.publish }}</span>
      </fieldset>

      <p class="cf-privacy">
        Your answers go to {{ named ? withStop(brandName) : 'us.' }} TrustClap stores them on {{ named ? possessive(brandName) : 'our' }} behalf. Contact details are never shown publicly.
        <template v-if="brand.privacy_url"><a :href="brand.privacy_url" target="_blank" rel="noopener">{{ named ? possessive(brandName) : 'Our' }} privacy policy</a> ·</template>
        <a :href="privacyUrl" target="_blank" rel="noopener">TrustClap's privacy policy</a>
      </p>

      <div class="cf-honeypot" aria-hidden="true">
        <label :for="fieldId('website')">Leave this empty</label>
        <input :id="fieldId('website')" v-model="website" type="text" tabindex="-1" autocomplete="off">
      </div>

      <button type="submit" class="cf-button cf-submit" :disabled="sending">{{ sending ? 'Sending…' : 'Send my feedback' }}</button>
      <a v-if="attribution" :href="landingUrl" class="cf-attribution" target="_blank" rel="noopener">Collected with TrustClap</a>
    </form>
  </div>
</template>

<script>
import StarRating from './StarRating.vue';
import { brandVars, initials } from '../lib/color';
import { possessive, withStop } from '../lib/format';

const EMAIL = /^[^@\s]{1,64}@[^@\s]{1,190}\.[^@\s]{2,}$/;
let instance = 0;

export function defaultFields() {
  return { rating: 'optional', name: 'required', title: 'optional', company: 'optional', photo: 'optional', contact: 'optional' };
}

export default {
  name: 'CollectFormPreview',
  components: { StarRating },
  props: {
    form: { type: Object, default: () => ({}) },
    brand: { type: Object, default: () => ({}) },
    mode: { type: String, default: 'preview' },
    attribution: { type: Boolean, default: false },
    landingUrl: { type: String, default: '' },
    privacyUrl: { type: String, default: '' },
    sending: { type: Boolean, default: false },
    error: { type: String, default: '' },
    result: { type: Object, default: null },
    compact: { type: Boolean, default: false },
    maxChars: { type: Number, default: 3000 }
  },
  data() {
    instance += 1;
    return {
      uid: 'cf' + instance, answers: {}, rating: null, name: '', jobTitle: '', company: '', contactEmail: '',
      photo: null, photoUrl: '', publish: this.mode === 'preview' ? true : null,
      show: { name: false, title: false, company: false, photo: false }, website: '', errors: {}, photoError: '',
      tried: false, copied: false
    };
  },
  computed: {
    content() { return this.form || {}; },
    questions() { return (this.content.questions || []).filter(q => q && q.label); },
    fields() { return Object.assign(defaultFields(), this.content.fields || {}); },
    // Before a name is typed (first run), "Your business" is only the header's label; the sentences speak as "we".
    named() { return !!(this.brand && this.brand.name); },
    brandName() { return this.named ? this.brand.name : 'Your business'; },
    monogram() { return initials(this.brandName); },
    vars() { return brandVars(this.brand && this.brand.color); },
    nameRequired() { return this.fields.name === 'required' && !this.content.allow_anonymous; },
    hasAboutYou() { return ['name', 'title', 'company', 'photo', 'contact'].some(key => this.fields[key] !== 'off'); },
    defaultThanks() { return 'Your feedback means a lot to us.'; },
    detailOptions() {
      const map = [['name', 'name'], ['title', 'job title'], ['company', 'company'], ['photo', 'photo']];
      return map.filter(([key]) => this.fields[key] !== 'off').map(([key, label]) => ({ key, label }));
    },
    // Everything validate() reads.
    checked() {
      return [JSON.stringify(this.answers), this.rating, this.name, this.jobTitle, this.company, this.contactEmail,
        this.photo, this.publish];
    }
  },
  watch: {
    // After a first attempt to send, each message goes as soon as its field is fixed (and comes back if it breaks).
    checked() { if (this.tried) this.validate(); },
    publish(value) {
      if (value !== true) this.show = { name: false, title: false, company: false, photo: false };
    },
    // After sending, focus moves to "Thank you" so screen readers announce it and keyboard users start there.
    result(value) {
      if (value) this.$nextTick(() => { if (this.$refs.done) this.$refs.done.focus(); });
    }
  },
  beforeDestroy() {
    if (this.photoUrl) URL.revokeObjectURL(this.photoUrl);
  },
  methods: {
    withStop,
    possessive,
    fieldId(name) { return this.uid + '-' + name; },
    choosePhoto(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = '';
      this.photoError = '';
      if (!file) return;
      if (['image/png', 'image/jpeg', 'image/webp'].indexOf(file.type) < 0 || file.size > 8 * 1024 * 1024) {
        this.photoError = 'That photo couldn\'t be read. Try a JPG or PNG under 8 MB, or send without a photo.';
        return;
      }
      if (this.photoUrl) URL.revokeObjectURL(this.photoUrl);
      this.photo = file;
      this.photoUrl = URL.createObjectURL(file);
    },
    clearPhoto() {
      if (this.photoUrl) URL.revokeObjectURL(this.photoUrl);
      this.photo = null;
      this.photoUrl = '';
    },
    removePhoto() {
      this.clearPhoto();
      this.photoError = '';
    },
    // Keeps what the customer typed when the server reports a problem with the photo. The message is kept apart from
    // the field checks, so it stays until another photo is chosen or removed.
    photoFailed(message) {
      this.clearPhoto();
      this.photoError = message || 'That photo couldn\'t be read. Try a JPG or PNG under 8 MB, or send without a photo.';
    },
    validate() {
      const errors = {};
      this.questions.forEach(question => {
        const text = (this.answers[question.id] || '').trim();
        if (question.required && !text) errors['q:' + question.id] = 'Please answer this question.';
        else if (text.length > this.maxChars) errors['q:' + question.id] = 'Please keep this under ' + this.maxChars + ' characters.';
      });
      if (this.questions.length && !this.questions.some(q => (this.answers[q.id] || '').trim())) {
        errors['q:' + this.questions[0].id] = errors['q:' + this.questions[0].id] || 'Please answer at least one question.';
      }
      if (this.fields.rating === 'required' && !this.rating) errors.rating = 'Please choose a rating.';
      if (this.fields.name !== 'off' && this.nameRequired && !this.name.trim()) errors.name = 'Please add your name.';
      if (this.fields.title === 'required' && !this.jobTitle.trim()) errors.title = 'Please add your job title.';
      if (this.fields.company === 'required' && !this.company.trim()) errors.company = 'Please add your company.';
      if (this.fields.photo === 'required' && !this.photo) errors.photo = 'Please add a photo.';
      const email = this.contactEmail.trim();
      if (this.fields.contact === 'required' && !email) errors.contact = 'Please add your email.';
      else if (email && !EMAIL.test(email)) errors.contact = 'Please check your email address.';
      if (this.publish !== true && this.publish !== false) errors.publish = 'Please choose whether we may publish your answers.';
      this.errors = errors;
      return !Object.keys(errors).length;
    },
    submit() {
      if (this.mode !== 'live' || this.sending) return;
      this.tried = true;
      if (!this.validate()) {
        this.$nextTick(() => {
          const first = this.$el.querySelector('.invalid, [aria-invalid="true"]');
          if (first && first.focus) first.focus();
          else if (first && first.scrollIntoView) first.scrollIntoView({ block: 'center' });
        });
        return;
      }
      const publish = this.publish === true;
      const consent = { publish, show_name: publish && this.show.name && !!this.name.trim(),
        show_title: publish && this.show.title && !!this.jobTitle.trim(),
        show_company: publish && this.show.company && !!this.company.trim(), show_photo: publish && this.show.photo && !!this.photo };
      this.$emit('submit', {
        answers: this.questions.map(q => ({ question_id: q.id, text: (this.answers[q.id] || '').trim() })).filter(a => a.text),
        rating: this.fields.rating !== 'off' ? this.rating : null,
        name: this.name.trim() || null, job_title: this.jobTitle.trim() || null, company: this.company.trim() || null,
        contact_email: this.contactEmail.trim() || null, consent, website: this.website
      }, this.photo);
    },
    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.result.withdrawal_url);
      } catch (error) {
        const input = this.$el.querySelector('.cf-link');
        if (input) { input.select(); document.execCommand('copy'); }
      }
      this.copied = true;
      window.setTimeout(() => { this.copied = false; }, 2200);
    }
  }
};
</script>

<style scoped>
.cf{--cf-ink:#1F2328;--cf-body:#4F4A45;--cf-muted:#6B635B;--cf-line:#E6DED2;color:var(--cf-ink);font:400 16px/1.55 var(--font-body);
  background:#fff;max-width:600px;margin:0 auto;padding:28px 22px 30px;box-sizing:border-box;text-align:left}
.cf *{box-sizing:border-box}
.cf-brand{display:flex;align-items:center;gap:10px;margin-bottom:22px}
.cf-logo{width:40px;height:40px;object-fit:contain;border-radius:10px}
.cf-monogram{display:inline-grid;place-items:center;width:40px;height:40px;border-radius:12px;background:var(--brand);color:var(--brand-on);font:700 15px var(--font-body);letter-spacing:.02em}
.cf-brand-name{font:700 15px/1.3 var(--font-body);color:var(--brand-text);overflow-wrap:anywhere}
.cf-headline{margin:0 0 10px;font:500 28px/1.18 var(--font-display);letter-spacing:-.4px;color:var(--cf-ink);overflow-wrap:anywhere}
.cf-intro{margin:0 0 10px;color:var(--cf-body)}
.cf-time{display:flex;align-items:center;gap:6px;margin:0 0 22px;font-size:13.5px;color:var(--cf-muted)}
.cf-field{margin-bottom:18px}
/* The shell styles every <label> with !important (AppShell.vue); these labels set their own type. */
.cf-label{display:block;margin:0 0 6px!important;font-weight:600!important;font-size:inherit!important;color:var(--cf-ink)!important;line-height:1.4}
.cf-optional{font-weight:500;color:var(--cf-muted)}
.cf-help{display:block;margin:-2px 0 8px;font-size:14px;line-height:1.45;color:var(--cf-muted)}
.cf-input{display:block;width:100%;min-height:48px;padding:12px 14px;border:1px solid #8C8378;border-radius:12px;background:#fff;color:var(--cf-ink);
  font:400 16px/1.5 var(--font-body);outline:none;transition:border-color .15s ease,box-shadow .15s ease}
/* A 2px ring in the brand colour darkened to 4.5:1 on white, so focus shows clearly whatever the brand colour. */
.cf-input:focus{border-color:var(--brand-text);box-shadow:0 0 0 2px var(--brand-text);outline:none}
.cf-input.invalid{border-color:#B42318}
.cf-textarea{resize:vertical}
.cf-preview .cf-textarea{resize:none}
.cf-count{margin-top:4px;text-align:right;font-size:12.5px;color:var(--cf-muted)}
.cf-field-error{display:block;margin-top:6px;font-size:14px;color:#B42318;font-weight:500}
.cf-error-box{margin:0 0 18px;padding:12px 14px;border-radius:12px;background:#FBE9E7;color:#8A1C13;border:1px solid rgba(180,35,24,.3);font-size:15px}
.cf-group{margin:6px 0 4px;padding-top:16px;border-top:1px solid var(--cf-line)}
.cf-group-title{margin:0 0 12px;font:700 13px/1 var(--font-body);letter-spacing:.08em;text-transform:uppercase;color:var(--cf-muted)}
.cf-photo{display:flex;align-items:center;flex-wrap:wrap;gap:12px}
.cf-photo-thumb{width:52px;height:52px;border-radius:50%;object-fit:cover;border:1px solid var(--cf-line)}
.cf-photo-thumb.empty{display:inline-grid;place-items:center;background:#F3EEE6;color:#8C8378;font-size:26px}
.cf-file{position:relative;overflow:hidden;margin:0!important;font-size:14px!important;font-weight:700!important;color:var(--brand-text)!important}
.cf-file input{position:absolute;inset:0;opacity:0;cursor:pointer}
.cf-file:focus-within{outline:2px solid var(--brand);outline-offset:2px}
.cf-link-button{border:0;background:none;color:var(--cf-muted);font:600 14px var(--font-body);text-decoration:underline;cursor:pointer;min-height:40px}
.cf-consent{margin:8px 0 18px;padding:18px 18px 14px;border:1.5px solid var(--brand-line);border-radius:16px;background:var(--brand-soft);min-width:0}
.cf-consent.invalid{border-color:#B42318;background:#FDF2F0}
/* Floated so the question sits inside the box instead of on its border. */
.cf-consent-title{float:left;width:100%;padding:0;margin-bottom:6px;font:600 17px/1.35 var(--font-body);color:var(--cf-ink)}
.cf-consent>*:not(.cf-consent-title){clear:both}
.cf-choice{display:flex;align-items:center;gap:10px;min-height:44px;padding:4px 0;margin:0!important;font-weight:500!important;font-size:inherit!important;color:var(--cf-ink)!important;cursor:pointer}
.cf-choice input,.cf-check input{width:20px;height:20px;margin:0;accent-color:var(--brand);flex:0 0 auto}
.cf-details{margin:2px 0 8px 30px;padding:10px 14px;border-radius:12px;background:#fff;border:1px solid var(--cf-line)}
.cf-details-title{margin:0 0 4px;font-size:14px;color:var(--cf-muted)}
.cf-check{display:flex;align-items:center;gap:10px;min-height:40px;margin:0!important;font-weight:400!important;font-size:inherit!important;color:var(--cf-ink)!important;cursor:pointer}
.cf-privacy{margin:0 0 20px;font-size:13.5px;line-height:1.55;color:var(--cf-muted)}
.cf-privacy a{color:var(--brand-text)}
.cf-honeypot{position:absolute;left:-10000px;width:1px;height:1px;overflow:hidden}
.cf-button{display:inline-flex;align-items:center;justify-content:center;gap:8px;min-height:48px;padding:12px 22px;border:1px solid transparent;border-radius:12px;
  background:var(--brand);color:var(--brand-on);font:700 16px/1.2 var(--font-body);cursor:pointer;text-decoration:none;transition:filter .15s ease}
.cf-button:hover{filter:brightness(.93)}
.cf-button:disabled{opacity:.7;cursor:progress}
.cf-button.small{min-height:40px;padding:8px 14px;font-size:14px;border-radius:10px}
.cf-button.ghost{background:#fff;color:var(--brand-text);border-color:var(--brand-line)}
.cf-submit{width:100%}
.cf-attribution{display:block;margin-top:18px;text-align:center;font-size:12px;color:var(--cf-muted);text-decoration:none}
.cf-attribution:hover{text-decoration:underline}
.cf-done{text-align:center}
.cf-done .cf-headline:focus{outline:none}
.cf-done .cf-brand{justify-content:center}
.cf-done-mark{display:inline-grid;place-items:center;width:64px;height:64px;margin:6px 0 16px;border-radius:50%;background:var(--brand-soft);color:var(--brand-text);font-size:34px}
.cf-withdraw{margin-top:22px;padding:16px;border-radius:14px;background:#F3EEE6;text-align:left;font-size:14.5px}
.cf-withdraw p{margin:0 0 10px;color:var(--cf-body)}
.cf-link-row{display:flex;gap:8px}
.cf-link{flex:1 1 auto;min-width:0;min-height:40px;padding:8px 10px;border:1px solid #D4C9B8;border-radius:10px;background:#fff;font:400 13.5px var(--font-body);color:var(--cf-ink)}
.cf-preview{pointer-events:none;user-select:none}
@media(max-width:420px){.cf{padding:22px 16px 26px}.cf-headline{font-size:25px}.cf-details{margin-left:8px}}
/* Compact: the phone-sized preview inside the app. Tight enough that the permission choice and the send button
   (in the brand colour) fit in the frame. Help text and "optional" markers are left out of this preview only. */
.cf.compact{padding:16px 14px 18px;font-size:13px}
.compact .cf-brand{margin-bottom:14px;gap:8px}
.compact .cf-logo,.compact .cf-monogram{width:34px;height:34px;border-radius:10px;font-size:13px}
.compact .cf-brand-name{font-size:14px}
.compact .cf-headline{font-size:20px;margin-bottom:6px}
.compact .cf-intro{margin-bottom:6px;line-height:1.45}
.compact .cf-time{margin-bottom:12px;font-size:11.5px}
.compact .cf-field{margin-bottom:10px}
.compact .cf-label{margin-bottom:4px!important}
.compact .cf-help,.compact .cf-group-title,.compact .cf-optional,.compact .cf-count,.compact ::v-deep .star-optional{display:none}
.compact .cf-input{min-height:36px;padding:7px 10px;font-size:12.5px;border-radius:9px}
.compact .cf-textarea{min-height:40px;height:40px}
.compact .cf-consent{padding:10px 12px 6px;margin:4px 0 10px;border-radius:12px}
.compact .cf-consent-title{font-size:13.5px;margin-bottom:2px}
.compact .cf-choice{min-height:28px;font-size:12.5px!important}
.compact .cf-choice input,.compact .cf-check input{width:15px;height:15px}
.compact .cf-details{margin-left:22px;padding:6px 10px}
.compact .cf-check{min-height:26px;font-size:12.5px!important}
.compact .cf-privacy{font-size:11px;line-height:1.45;margin-bottom:10px}
.compact .cf-submit{min-height:38px;padding:8px 14px;font-size:13.5px;border-radius:10px}
.compact .cf-attribution{margin-top:10px;font-size:11px}
@media(prefers-reduced-motion:reduce){.cf-input,.cf-button{transition:none}}
</style>
